import { useRef } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import {
  CardSelectableContainer,
  CardSelectableItem,
} from '@factorialco/f0-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useCreateCalendarSync } from 'modules/cronofy/components/ConnectMyCalendar/useCreateCalendarSync'
import { useAccessCalendarSync } from 'modules/personal_settings/components/Settings/ExternalCalendar/useAccessCalendarSync'
import { SessionData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'

const CALENDAR_VALUE = 'calendar'

interface CalendarInvitesCardProps {
  data: SessionData
  setCurrentData: (value: SessionData) => void
}

const CalendarInvitesCard = ({ data, setCurrentData }: CalendarInvitesCardProps) => {
  const initialSendCalendarInvites = useRef(data.sendCalendarInvites ?? false)
  const {
    access: { id: accessId },
  } = useCurrentSession()
  const { accessCalendarSync, loading } = useAccessCalendarSync(accessId)
  const { createCalendarSync, loading: creating } = useCreateCalendarSync()
  const isCalendarConnected = !!accessCalendarSync?.sub

  if (loading) return null

  const handleChange = (value: string | undefined) => {
    setCurrentData({ ...data, sendCalendarInvites: value === CALENDAR_VALUE })
  }

  const handleConnectCalendar = async () => {
    if (accessCalendarSync?.oauthUrl) {
      window.location.href = accessCalendarSync.oauthUrl
      return
    }
    const result = await createCalendarSync({ accessId })
    const handled = handleResult(result, () => undefined)
    if (handled.success && handled.data.calendarSync && handled.data.calendarSync.oauthUrl) {
      window.location.href = handled.data.calendarSync.oauthUrl
    }
  }

  const description = isCalendarConnected
    ? i18n.t(
        'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.synced_description',
        { profileName: accessCalendarSync.profileName ?? '' }
      )
    : i18n.t(
        'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.description'
      )

  const items: CardSelectableItem<string>[] = [
    {
      value: CALENDAR_VALUE,
      title: i18n.t(
        'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.title'
      ),
      description,
      disabled: !isCalendarConnected,
    },
  ]

  return (
    <Box gap="s8">
      <CardSelectableContainer
        items={items}
        isToggle
        value={data.sendCalendarInvites ? CALENDAR_VALUE : undefined}
        onChange={handleChange}
        disabled={!isCalendarConnected}
      />
      {!isCalendarConnected && (
        <F0Alert
          variant="warning"
          title={i18n.t(
            'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.not_connected_title'
          )}
          description={i18n.t(
            'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.not_connected_description'
          )}
          action={{
            label: i18n.t(
              'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.connect_button'
            ),
            onClick: handleConnectCalendar,
            disabled: creating,
          }}
        />
      )}
      {isCalendarConnected && initialSendCalendarInvites.current && !data.sendCalendarInvites && (
        <F0Alert
          variant="warning"
          title={i18n.t(
            'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.cancel_invites_title'
          )}
          description={i18n.t(
            'trainings.trainings_revamp.sessions.new_session_modal.calendar_invites.cancel_invites_description'
          )}
        />
      )}
    </Box>
  )
}

export default CalendarInvitesCard
